import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import TooltipRateGroupsMonth from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-rate-groups-month';
import type { Period } from 'lumen/models';
import type { RateGroup } from 'lumen/models/pa-subscription-meta-info';

export interface RateGroupsMonthColumn extends CustomEmberTableColumn {
  rateGroups: RateGroup[];
}

interface HotelPricingAssistantTableCellsTdRateGroupsMonthSignature {
  Args: {
    column: RateGroupsMonthColumn;
    value: Period;
  };
}

export default class HotelPricingAssistantTableCellsTdRateGroupsMonth extends Component<HotelPricingAssistantTableCellsTdRateGroupsMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  /* The count of rate groups restricted by MinLOS; "Open" when every known value is
     unrestricted; "--" when no value is known, so a gap never reads as "Open". */
  get displayValue(): string {
    const restrictedCount = this.minLosValues.filter(
      (value) => value != null && value > 0,
    ).length;

    if (restrictedCount > 0) {
      return this.intl.formatNumber(restrictedCount);
    }

    if (!this.isMissing) {
      return this.intl.t('channel_pricing.restriction.none');
    }

    return this.intl.t('general.missing');
  }

  /* Until both requests land, or while a rate group's MinLOS push is in flight,
     `rateGroupMinLos` reads a stale or partial value. */
  get isLoading(): boolean {
    return (
      this.pricingAssistantDataRequestService.restrictionsRequestTask
        .isRunning ||
      this.pricingAssistantDataRequestService
        .restrictionsRecommendationsRequestTask.isRunning ||
      this.args.column.rateGroups.some(
        ({ rateGroupId }) =>
          this.args.value.rateGroupMinLos(rateGroupId).isLoading,
      )
    );
  }

  get isMissing(): boolean {
    return this.minLosValues.every((value) => value == null);
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.value.fromDate);
  }

  get minLosValues(): (number | null)[] {
    return this.args.column.rateGroups.map(
      ({ rateGroupId }) => this.args.value.rateGroupMinLos(rateGroupId).value,
    );
  }

  <template>
    {{#if this.isLoading}}
      <div
        class="loading-pulse"
        data-test-id="td-rate-groups-month-loading"
      ></div>
    {{else}}
      <div
        class="mr-500
          {{if
            this.isPast
            'text-disabled'
            (if this.isMissing 'text-neutral-subdued')
          }}"
        data-test-id="td-rate-groups-month"
        tabindex="0"
      >
        {{this.displayValue}}
        <TooltipRateGroupsMonth
          @period={{@value}}
          @rateGroups={{@column.rateGroups}}
        />
      </div>
    {{/if}}
  </template>
}
